/**
 * SujetBanner.tsx — S221 A4
 * Bannière du sujet de discussion, extraite de WorkspaceGreffier.tsx.
 * Réutilisée dans CahierLivrable mode "discussion" et WorkspaceGreffier.
 */

import { useState } from "react";
import {
  Target,
  CheckCircle2,
  MessageSquare,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import type { ElementSujet, ElementAvis } from "../hooks/useDiscussionElements";

// ── Avis repliable (réutilisé sous chaque élément avec avis) ────
export function AvisInline({ avis }: { avis: ElementAvis[] }) {
  const [open, setOpen] = useState(false);
  if (avis.length === 0) return null;
  return (
    <div className="mt-1">
      <button
        onClick={() => setOpen(!open)}
        className="text-[10px] text-gray-500 hover:text-gray-700 flex items-center gap-1 cursor-pointer font-medium"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        {avis.length} avis
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {avis.map((a) => (
            <div key={a.id} className="flex items-start gap-1.5 bg-white/60 rounded px-2 py-1 border border-gray-100">
              <MessageSquare className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
              <div className="text-[10px]">
                <span className="font-medium text-gray-700">{a.bot}</span>
                <span className="text-gray-400"> · {a.position}</span>
                {Array.isArray(a.points) && a.points.length > 0 && (
                  <p className="text-gray-500 mt-0.5">{a.points.slice(0, 2).join(" · ")}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Bannière du sujet ────────────────────────────────────────────
export function SujetBanner({
  sujet, onConfirmer, onPreciser,
}: {
  sujet: ElementSujet | null;
  onConfirmer?: () => Promise<{ ok: boolean; status?: number }>;
  onPreciser?: () => void;
}) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  if (!sujet) return null;

  const handleConfirmer = async () => {
    if (!onConfirmer) return;
    setErreur(null);
    setEnCours(true);
    try {
      const res = await onConfirmer();
      if (!res.ok) {
        if (res.status === 403) setErreur("Rôle insuffisant");
        else if (res.status === 409) setErreur("Aucun sujet à confirmer");
        else setErreur("Erreur lors de la confirmation");
      }
    } catch { setErreur("Erreur réseau"); }
    finally { setEnCours(false); }
  };

  return (
    <div className="border rounded-xl overflow-hidden shadow-sm bg-white">
      <div className="bg-gradient-to-r from-slate-600 to-slate-500 px-4 py-2.5 flex items-center gap-2 border-b">
        <Target className="h-4 w-4 text-white" />
        <span className="text-sm font-bold text-white">Sujet</span>
        <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${
          sujet.confirme ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
        }`}>
          {sujet.confirme ? "Confirmé" : "À confirmer"}
        </span>
      </div>
      <div className="p-4 space-y-2">
        <p className="text-sm text-gray-900 font-medium">{sujet.objectif}</p>
        {sujet.tension && <p className="text-xs text-gray-500">Tension : {sujet.tension}</p>}
        {sujet.resultat_vise && <p className="text-xs text-gray-500">Résultat visé : {sujet.resultat_vise}</p>}
        <AvisInline avis={sujet.avis || []} />
        {!sujet.confirme && (onConfirmer || onPreciser) && (
          <div className="flex items-center gap-2 pt-1">
            {onConfirmer && (
              <button
                onClick={handleConfirmer}
                disabled={enCours}
                className="text-[10px] px-3 py-1.5 rounded-full border border-green-300 bg-white text-green-700 hover:bg-green-50 font-medium cursor-pointer transition-colors flex items-center gap-1 disabled:opacity-50"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {enCours ? "Confirmation…" : "Oui, c'est ça"}
              </button>
            )}
            {onPreciser && (
              <button
                onClick={onPreciser}
                className="text-[10px] px-3 py-1.5 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 font-medium cursor-pointer transition-colors flex items-center gap-1"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Je précise
              </button>
            )}
          </div>
        )}
        {erreur && <p className="text-[10px] text-red-600">{erreur}</p>}
      </div>
    </div>
  );
}
